// Логика спецификации над api и хранилищем (ТЗ 12.6): загрузка спецификации, правка позиций с
// обновлением вычисляемых полей из ответа сервера, пакетные действия, переходы статусов с
// подтверждением (ТЗ 6.9), выбор отклика, задания AI с опросом раз в 3 секунды (ТЗ 5.1) — по
// нескольку одновременно, с отменой (ТЗ 4.1).
//
// Все операции записи идут через общий механизм показа ошибки (model/apiRun): штатный разбор
// ошибок выводит только 403 и 404, поэтому без него отказ 422 («комиссия зафиксирована», «проект
// в архиве», ограничения ввода) и 429 (лимит заданий AI) прошли бы молча, а промис упал бы
// необработанным. Тексты 422 показываются как есть (ТЗ 6.9). Отказ — это `null` в ответе.
//
// Сгенерировано Claude.
import store from '@/store';
import popup from '@/model/popup';
import { apiRun } from '@/model/apiRun';
import specificationApi from '@/api/specification';
import specItemApi from '@/api/specItem';
import specAiJobApi from '@/api/specAiJob';
import specHistoryApi from '@/api/specHistory';
import fileApi from '@/api/file';
import { itemStatusTitle } from '@/utils/spec';

/** Интервал опроса активного задания AI (ТЗ 5.1: раз в 3 секунды). */
const POLL_INTERVAL = 3000;

/**
 * Задержка перечитывания спецификации после правки позиции: свод и итоги категорий считает сервер
 * (ТЗ 7.2), а ответ правки содержит только саму позицию. Пачка быстрых правок подряд даёт одно
 * перечитывание, а не по одному на каждую.
 */
const REFRESH_DELAY = 400;

/** Поля позиции, от которых зависит свод (ТЗ 7.1, 7.2): после их правки свод перечитывается. */
const SUMMARY_FIELDS = ['quantity', 'plannedPrice'];

/** Таймеры опроса заданий AI по их идентификаторам: заданий может быть несколько одновременно. */
const pollTimers = {};
let refreshTimer = null;

export default {
  /** Спецификация проекта; при первом обращении её создаёт сервер (ТЗ 4.1). */
  fetch(projectId, withPreloader = true) {
    return specificationApi.fetchByProject(projectId, withPreloader)
      .then((specification) => {
        store.commit('specification/setProjectId', projectId);
        store.commit('specification/setSpecification', specification);
        return specification;
      });
  },

  /**
   * Спецификация проекта из хранилища; с сервера читается, только если её там ещё нет или она от
   * другого проекта. Вкладкам «Ведомость поставки» и «Чаты» нужен лишь идентификатор.
   */
  ensure(projectId) {
    const { specification, projectId: loaded } = store.state.specification;
    if (specification && loaded === projectId) return Promise.resolve(specification);
    return this.fetch(projectId, false);
  },

  /** Перечитать спецификацию по уже известному проекту (после заданий AI и импорта). */
  refresh(projectId = null) {
    const id = projectId ?? store.state.specification.projectId;
    if (!id) return Promise.resolve(null);
    return this.fetch(id, false);
  },

  /**
   * Отложенное перечитывание свода: правка позиции отдаёт только позицию, а свод и итоги
   * категорий приходят вместе со спецификацией (ТЗ 4.1, 7.2).
   */
  scheduleRefresh() {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      this.refresh();
    }, REFRESH_DELAY);
  },

  get specification() {
    return store.state.specification.specification;
  },

  createItem(params) {
    return apiRun(
      () => specItemApi.create(params),
      'Не удалось создать позицию',
    ).then((item) => {
      if (!item) return null;
      store.commit('specification/setItem', item);
      this.scheduleRefresh();
      return item;
    });
  },

  /** Каждое сохранение ячейки — отдельный запрос; значения берутся из ответа (ТЗ 6.2). */
  patchItem(id, params) {
    return apiRun(
      () => specItemApi.update(id, params),
      'Не удалось сохранить изменение',
    ).then((item) => {
      if (!item) return null;
      store.commit('specification/setItem', item);
      if (Object.keys(params).some((field) => SUMMARY_FIELDS.includes(field))) {
        this.scheduleRefresh();
      }
      return item;
    });
  },

  /** Удаление позиции — необратимо, поэтому с подтверждением (ТЗ 6.9). */
  async deleteItem(item) {
    const answer = await popup.addConfirm(
      `Удалить позицию «${item.name}»?`,
      'Вместе с позицией удалятся её отклики; поставщики незавершённых запросов получат уведомление.',
    );
    if (!answer) return false;

    const result = await apiRun(
      () => specItemApi.delete(item.id),
      'Не удалось удалить позицию',
    );
    if (result === null) return false;

    store.commit('specification/removeItem', item.id);
    this.scheduleRefresh();
    return true;
  },

  /** Пакетное действие; ответ — вся спецификация со сводом (ТЗ 4.1). */
  batch(specificationId, params) {
    return apiRun(
      () => specificationApi.batch(specificationId, params),
      'Не удалось выполнить действие по выделенным позициям',
    ).then((specification) => {
      if (!specification) return null;
      store.commit('specification/setSpecification', specification);
      return specification;
    });
  },

  /** Порядок категорий спецификации (перетаскивание групп, ТЗ 6.2). */
  setCategoryOrder(specificationId, categoryOrder) {
    return apiRun(
      () => specificationApi.update(specificationId, { categoryOrder }),
      'Не удалось сохранить порядок категорий',
    ).then((specification) => {
      if (!specification) return null;
      this.setSpecification(specification);
      return specification;
    });
  },

  /**
   * Спецификация из ответа операции записи (настройки, порядок категорий): она приходит целиком
   * со сводом и позициями, поэтому перечитывать её отдельным запросом не нужно.
   */
  setSpecification(specification) {
    const current = store.state.specification.specification;
    store.commit('specification/setSpecification', current
      ? { ...current, ...specification }
      : specification);
  },

  /** Переход статуса позиции — необратим, поэтому с подтверждением (ТЗ 2.5, 6.9). */
  async transition(item, action) {
    const answer = await popup.addConfirm(
      `Перевести позицию «${item.name}» в статус «${itemStatusTitle(action)}»?`,
      'Переход необратим.',
    );
    if (!answer) return null;

    const updated = await apiRun(
      () => specItemApi.transition(item.id, action),
      'Не удалось сменить статус позиции',
    );
    if (!updated) return null;

    store.commit('specification/setItem', updated);
    return updated;
  },

  /**
   * «Вернуть в исходное состояние» (ТЗ 4.1, 2.5): с подтверждением — после утверждения об этом
   * узнаёт поставщик выбранного отклика. Свод перечитывается: скидка заказчику обнуляется.
   */
  async resetItem(item) {
    const answer = await popup.addConfirm(
      `Вернуть позицию «${item.name}» в исходное состояние?`,
      'Позиция вернётся в «Есть отклики», выбранный отклик снова станет одним из предложений, скидка заказчику обнулится.',
    );
    if (!answer) return null;

    const updated = await apiRun(
      () => specItemApi.reset(item.id),
      'Не удалось вернуть позицию в исходное состояние',
    );
    if (!updated) return null;

    store.commit('specification/setItem', updated);
    this.scheduleRefresh();
    return updated;
  },

  /**
   * Решение по аналогу, предложенному AI: `apply` — заменить позицию, `reject` — отклонить.
   * Свод перечитывается: меняются плановый бюджет и сумма с аналогами.
   */
  async decideAnalog(item, decision) {
    const updated = await apiRun(
      () => (decision === 'apply' ? specItemApi.analogApply(item.id) : specItemApi.analogReject(item.id)),
      decision === 'apply' ? 'Не удалось заменить позицию на аналог' : 'Не удалось отклонить аналог',
    );
    if (!updated) return null;

    store.commit('specification/setItem', updated);
    this.scheduleRefresh();
    return updated;
  },

  /** Порог цены и уточнения для AI перед плановым бюджетом. */
  setBudgetSettings(specificationId, { budgetLimit, budgetHint }) {
    return apiRun(
      () => specificationApi.update(specificationId, { budgetLimit, budgetHint }),
      'Не удалось сохранить порог цены',
    ).then((specification) => {
      if (!specification) return null;
      this.setSpecification(specification);
      return specification;
    });
  },

  rewardReceived(itemId, date) {
    return apiRun(
      () => specItemApi.rewardReceived(itemId, date),
      'Не удалось отметить вознаграждение',
    ).then((item) => {
      if (!item) return null;
      store.commit('specification/setItem', item);
      return item;
    });
  },

  chooseOffer(itemId, params) {
    return apiRun(
      () => specItemApi.chooseOffer(itemId, params),
      'Не удалось выбрать отклик',
    ).then((item) => {
      if (!item) return null;
      store.commit('specification/setItem', item);
      this.scheduleRefresh();
      return item;
    });
  },

  changeDiscount(itemId, params) {
    return apiRun(
      () => specItemApi.changeDiscount(itemId, params),
      'Не удалось изменить разделение скидки',
    ).then((item) => {
      if (!item) return null;
      store.commit('specification/setItem', item);
      this.scheduleRefresh();
      return item;
    });
  },

  /** Фото позиции: общая загрузка с назначением «спецификация», затем привязка (ТЗ 9.1). */
  async uploadPhoto(itemId, file) {
    const uploaded = await apiRun(
      () => fileApi.createForSpecification(file),
      'Не удалось загрузить файл',
    );
    if (!uploaded) return null;
    return this.patchItem(itemId, { photo: `/api/files/${uploaded.id}` });
  },

  /** Создать задание AI и начать опрос его состояния (ТЗ 4.1, 5.1). */
  async startJob(params) {
    const job = await apiRun(
      () => specAiJobApi.create(params),
      'Не удалось запустить задание',
    );
    return this.trackJob(job);
  },

  /** «Найти цену заново» по одной позиции (ТЗ 4.1). */
  async findPrice(itemId) {
    const job = await apiRun(
      () => specItemApi.findPrice(itemId),
      'Не удалось запустить поиск цены',
    );
    return this.trackJob(job);
  },

  /** «Импортировать снова» файл из истории спецификации (ТЗ 6.2): новое задание импорта. */
  async reimport(historyId) {
    const job = await apiRun(
      () => specHistoryApi.reimport(historyId),
      'Не удалось запустить импорт',
    );
    return this.trackJob(job);
  },

  /**
   * Восстановление индикации после перезагрузки страницы: все активные задания спецификации
   * (ТЗ 4.1, 6.2) — их может быть несколько одновременно.
   */
  async restoreActiveJobs(specificationId) {
    const { items } = await specificationApi.fetchActiveJobs(specificationId);
    // (Claude) задания, оставшиеся в хранилище с прошлого захода, могли завершиться без опроса —
    // индикатор начинается с того, что сервер считает активным сейчас
    this.stopPolling();
    store.commit('specification/clearJobs');
    (items ?? []).forEach((job) => this.trackJob(job));
    return items ?? [];
  },

  /**
   * Отменить задание (ТЗ 4.1 «Отменить задание»): оно пропадает из индикатора. Если задание успело
   * завершиться, сервер отвечает 422 — тогда показывается его итоговое состояние.
   */
  async cancelJob(job) {
    const cancelled = await apiRun(
      () => specAiJobApi.cancel(job.id),
      'Не удалось отменить задание',
    );
    if (cancelled) {
      this.stopPolling(job.id);
      store.commit('specification/removeJob', job.id);
      return cancelled;
    }
    const actual = await specAiJobApi.fetchItem(job.id).catch(() => null);
    if (actual) this.onJobUpdate(actual);
    return null;
  },

  /** Убрать завершённое задание из индикатора. */
  dismissJob(job) {
    this.stopPolling(job.id);
    store.commit('specification/removeJob', job.id);
  },

  /** Задание в индикатор и опрос его состояния; `null` (отказ сервера) пропускается. */
  trackJob(job) {
    if (!job) return null;
    store.commit('specification/addJob', job);
    this.startPolling(job.id);
    return job;
  },

  /** Опрос задания раз в 3 секунды, пока queued/processing (ТЗ 5.1); у каждого задания свой. */
  startPolling(jobId) {
    if (pollTimers[jobId]) return;
    pollTimers[jobId] = setInterval(() => {
      specAiJobApi.fetchItem(jobId)
        .then((job) => this.onJobUpdate(job))
        .catch(() => { this.stopPolling(jobId); });
    }, POLL_INTERVAL);
  },

  /**
   * Новое состояние задания. Завершилось задание бюджета или фото — перечитываем спецификацию:
   * плановые цены, источники, признак низкой уверенности и фото пишет сервер, клиент их только
   * показывает (ТЗ 5.3, 5.4).
   */
  onJobUpdate(job) {
    if (job.status === 'cancelled') {
      this.stopPolling(job.id);
      store.commit('specification/removeJob', job.id);
      return;
    }
    store.commit('specification/setJob', job);
    if (job.status !== 'done' && job.status !== 'error') return;
    this.stopPolling(job.id);
    if (job.type !== 'import') this.refresh();
  },

  /** Остановить опрос одного задания или, без аргумента, всех (уход со страницы). */
  stopPolling(jobId = null) {
    const ids = jobId === null ? Object.keys(pollTimers) : [jobId];
    ids.forEach((id) => {
      clearInterval(pollTimers[id]);
      delete pollTimers[id];
    });
  },

  /** Текст состояния задания для индикатора в шапке (ТЗ 5.3, 5.4, 6.2). */
  jobStatusText(job) {
    if (!job) return '';

    const kind = {
      import: 'Импорт',
      budget: 'Плановый бюджет',
      images: 'Подбор фото',
      quote_match: 'Сопоставление КП',
    }[job.type] ?? 'Задание';

    if (job.status === 'queued' || job.status === 'processing') {
      return `${kind}: в обработке`;
    }

    if (job.status === 'error') {
      return `${kind} — ошибка: ${job.error || 'не удалось выполнить'}`;
    }

    const counts = this.jobCounts(job);

    if (!counts) return `${kind}: Готово`;

    const what = job.type === 'budget'
      ? `цены найдены для ${counts.done} из ${counts.total} позиций`
      : `фото подобраны для ${counts.done} из ${counts.total} позиций`;

    return counts.failed
      ? `${kind} — Готово частично: ${what}, ошибка по ${counts.failed}`
      : `${kind}: Готово: ${what}`;
  },

  /** Сколько позиций обработано, сколько всего, сколько в ошибочных пачках (ТЗ 5.3, 5.4). */
  jobCounts(job) {
    if (!job || !['budget', 'images'].includes(job.type)) return null;

    const requested = job.input?.itemIds ?? [];
    const results = job.result?.items ?? [];
    const failed = (job.result?.failedBatches ?? [])
      .reduce((acc, batch) => acc + (batch.itemIds ?? []).length, 0);

    const done = job.type === 'budget'
      ? results.filter((row) => row.price !== null && row.price !== undefined).length
      : results.filter((row) => (row.candidates ?? []).length > 0).length;

    return { done, total: requested.length || results.length + failed, failed };
  },

  /** Позиции ошибочных пачек — для кнопки «Повторить по ошибочным» (ТЗ 6.2). */
  failedItemIds(job) {
    return (job?.result?.failedBatches ?? [])
      .reduce((acc, batch) => [...acc, ...(batch.itemIds ?? [])], [])
      .map(Number);
  },
};
